import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getUser, setSession } from "@/lib/auth";
import { ChevronDown, FolderKanban, Check } from "lucide-react";

/**
 * Project switcher — Super-Admin can hop across projects and see the current one.
 * Non-Super users see a read-only label of their project.
 */
export const ProjectSwitcher = () => {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const ref = useRef(null);
  const user = getUser();

  useEffect(() => {
    if (!user) return;
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      const active = data.find((p) => p.id === user.active_project_id) || data[0];
      setCurrent(active);
    }).catch(() => setProjects([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const switchTo = async (p) => {
    if (!p || p.id === current?.id) { setOpen(false); return; }
    try {
      const { data } = await api.post(`/auth/switch-project/${p.id}`);
      setSession(data.token, data.user);
      setCurrent(p);
      toast.success(`Switched to ${p.name}`);
      setOpen(false);
      // Reload so all data refetches under the new project scope
      setTimeout(() => window.location.reload(), 400);
    } catch (e) { toast.error(e.response?.data?.detail || "Switch failed"); }
  };

  if (!user || projects.length === 0) return null;
  const isSuper = user.role === "SuperAdmin";
  if (!isSuper && projects.length === 1) {
    // Non-super users with only one project: show read-only pill
    return (
      <div className="hidden md:flex items-center gap-1.5 rounded-lg bg-neutral-800 border border-neutral-700 px-2.5 py-1.5">
        <FolderKanban className="h-3.5 w-3.5 text-neutral-400" />
        <span className="text-[11px] font-semibold text-neutral-300 truncate max-w-[180px]" data-testid="active-project-label">
          {current?.name || "—"}
        </span>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="project-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 px-2.5 py-1.5 transition-colors"
        title="Switch project"
      >
        <FolderKanban className="h-3.5 w-3.5 text-neutral-400" />
        <span className="text-[11px] font-semibold text-white truncate max-w-[180px]">
          {current?.name || "Select project"}
        </span>
        <ChevronDown className={`h-3 w-3 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[280px] rounded-xl bg-white shadow-2xl border border-gray-200 py-1 z-50" data-testid="project-switcher-menu">
          <div className="px-3 py-2 border-b border-gray-100 text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            {isSuper ? "All projects" : "Your project"}
          </div>
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => switchTo(p)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-red-50 transition-colors ${p.id === current?.id ? "bg-red-50" : ""}`}
              data-testid={`project-item-${p.code || p.id.slice(0, 8)}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
                  {p.name}
                  {p.is_default && <span className="text-[9px] uppercase tracking-widest font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Default</span>}
                </div>
                <div className="text-[11px] text-gray-500 truncate">{p.client_name || p.code}</div>
              </div>
              {p.id === current?.id && <Check className="h-4 w-4 text-brand-red shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
